'use client';

import { useEffect, useRef, useState } from 'react';
import type { SurfaceSize } from '@/lib/manual-vector';

/**
 * The real CSS size of a box, measured rather than assumed.
 *
 * Manual flight needs this in two places and they must agree: the control
 * surface decides whether it is a control at all from the box it is drawn in,
 * and the command bar decides whether to offer the MANUAL key at all from the
 * box the operator would fly in. Two implementations of "how big is that
 * column" is exactly how those two answers drift apart, so there is one.
 *
 * `ResizeObserver` and not a window `resize` listener, because the COP's own
 * layout changes are the ones that matter: opening the details panel narrows
 * the map column, and switching stage layout re-hosts it entirely, with no
 * window resize anywhere. Where the browser has no `ResizeObserver` at all the
 * window event is the honest fallback — it catches less, but it never reports
 * a size that is wrong.
 *
 * `size` is null until the first measurement lands. Null is not zero: a caller
 * must be able to tell "too small to fly from" apart from "not measured yet",
 * because refusing on the second one would refuse on ignorance.
 */
export function useMeasuredSize<T extends HTMLElement>(): {
  ref: React.RefObject<T | null>;
  size: SurfaceSize | null;
} {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<SurfaceSize | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize((prev) =>
        prev !== null && prev.widthPx === rect.width && prev.heightPx === rect.height
          ? prev
          : { widthPx: rect.width, heightPx: rect.height },
      );
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, size };
}
